// src/layouts/ProfileRailCard.tsx
//
// v3 cross-cutting rule: the user profile lives at the BOTTOM of the sidebar
// rail (as in roommatik.console.ui / Insight), not in the app bar. Translucent
// white overlay card on the solid teal rail — avatar inverted (white fill,
// teal initials), name + role, and a chevron opening a menu with the session
// countdown, the scoped ID (hotel/chain) with copy action and sign-out.
import React, { useCallback, useEffect, useState } from 'react';
import { useGetIdentity, useLogout, useTranslate } from 'react-admin';
import {
    Avatar,
    Box,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    AccessTime as TimeIcon,
    ContentCopy as CopyIcon,
    CheckCircle as CopiedIcon,
    Logout as LogoutIcon,
    UnfoldMoreRounded as UnfoldMoreIcon,
} from '@mui/icons-material';
import { decodeJWT } from '../utils/auth';

const ROLE_NAMES: Record<string, string> = {
    SuperAdmin: 'Super Administrator',
    GroupAdmin: 'Group Administrator',
    HotelAdmin: 'Hotel Administrator',
    Receptionist: 'Receptionist',
    Developer: 'Developer',
    User: 'User',
};

const getInitials = (name?: string): string => {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0].toUpperCase())
        .join('');
};

export const ProfileRailCard: React.FC = () => {
    const theme = useTheme();
    const translate = useTranslate();
    const logout = useLogout();
    const { data: identity } = useGetIdentity();

    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<string>('');
    const [isExpiringSoon, setIsExpiringSoon] = useState(false);
    const [copiedId, setCopiedId] = useState(false);

    const formatTimeRemaining = useCallback((seconds: number): string => {
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        if (minutes > 0) return `${minutes}m ${secs}s`;
        return `${secs}s`;
    }, []);

    const calculateTimeRemaining = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) { setTimeRemaining(''); return; }
        try {
            const decoded = decodeJWT(token);
            if (!decoded || !decoded.exp) { setTimeRemaining(''); return; }
            const timeLeft = decoded.exp - Math.floor(Date.now() / 1000);
            if (timeLeft <= 0) {
                setTimeRemaining(translate('auth.session_expired', 'Sesión expirada'));
                setIsExpiringSoon(true);
                return;
            }
            setIsExpiringSoon(timeLeft <= 300);
            setTimeRemaining(formatTimeRemaining(timeLeft));
        } catch {
            setTimeRemaining('');
        }
    }, [formatTimeRemaining, translate]);

    useEffect(() => {
        calculateTimeRemaining();
        const interval = setInterval(calculateTimeRemaining, 1000);
        return () => clearInterval(interval);
    }, [calculateTimeRemaining]);

    const handleCopyId = (value: string) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopiedId(true);
            setTimeout(() => setCopiedId(false), 2000);
        });
    };

    const userName = identity?.name || identity?.username || 'User';
    const userRole = identity?.role || 'User';
    const roleLabel = ROLE_NAMES[userRole] || userRole;

    // Scoped ID (hotel or chain) shown as a Keycloak-configuration aid.
    const scopedId: { label: string; prefix: string; value: string } | null = (() => {
        const hotelId = (identity as any)?.hotelId as string | undefined;
        const groupId = (identity as any)?.groupId as string | undefined;
        if (userRole === 'GroupAdmin' && groupId)
            return { label: 'Group ID', prefix: 'chain-id-', value: groupId };
        if ((userRole === 'HotelAdmin' || userRole === 'Receptionist' || userRole === 'Developer') && hotelId)
            return { label: 'Hotel ID', prefix: 'hotel-id-', value: hotelId };
        return null;
    })();

    return (
        <>
            <Box
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: '8px',
                    // Translucent white overlay card, not an opaque white block —
                    // stays part of the same solid-teal rail language.
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                }}
            >
                <Avatar
                    sx={{
                        width: 30,
                        height: 30,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        // Inverted from the usual teal-fill avatar so it reads
                        // clearly against the teal card behind it.
                        backgroundColor: theme.palette.common.white,
                        color: theme.palette.primary.dark,
                    }}
                >
                    {getInitials(userName)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontSize: '12.5px', fontWeight: 600, color: '#FFFFFF' }}>
                        {userName}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.7)' }}>
                        {roleLabel}
                    </Typography>
                </Box>
                <UnfoldMoreIcon sx={{ fontSize: '18px', color: 'rgba(255,255,255,0.7)' }} />
            </Box>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {timeRemaining && (
                    <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'center' }}>
                        <Chip
                            icon={<TimeIcon />}
                            label={timeRemaining}
                            size="small"
                            color={isExpiringSoon ? 'warning' : 'success'}
                            sx={{ fontWeight: 500, '& .MuiChip-icon': { fontSize: 16 } }}
                        />
                    </Box>
                )}
                {timeRemaining && <Divider sx={{ mb: 0.5 }} />}
                {scopedId && (
                    <Box sx={{ px: 2, py: 1, maxWidth: 240 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {scopedId.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontFamily: 'monospace',
                                    bgcolor: 'action.selected',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                title={`${scopedId.prefix}${scopedId.value}`}
                            >
                                {scopedId.value}
                            </Typography>
                            <Tooltip title={copiedId ? translate('ra.action.cloned', 'Copiado') : translate('ra.action.clone', 'Copiar')}>
                                <IconButton size="small" onClick={() => handleCopyId(`${scopedId.prefix}${scopedId.value}`)}>
                                    {copiedId ? <CopiedIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
                {scopedId && <Divider sx={{ mb: 0.5 }} />}
                <MenuItem
                    onClick={() => {
                        setAnchor(null);
                        logout();
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{translate('ra.auth.logout', 'Cerrar sesión')}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

export default ProfileRailCard;
